import { useState, useEffect, useMemo } from 'react'
import { searchCustomerDisplaySettings } from 'Api/core'
import { useCustomerDisplay } from '../context/CustomerDisplayContext'

// Normalize content structure to match expected format
function normalizeContent(items) {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    ...item,
    // Map API fields to expected fields
    Id: item.CustomerDisplaySettingId || item.Id,
    MediaUrl: item.FileUrl || item.MediaUrl,
    MediaType: item.MediaType?.toLowerCase() || 'image',
    IsActive:
      item.Status !== undefined
        ? item.Status
        : item.IsActive !== undefined
        ? item.IsActive
        : true,
    Order: item.DisplayOrder ?? item.Order,
    CreatedAt: item.StartDate || item.CreatedAt,
  }))
}

// Start and end dates are configured as calendar days. Settings stores them as
// UTC midnight, so only the date part is kept and rebuilt in local time.
function getLocalDay(value, { endOfDay = false } = {}) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!match) return new Date(value)

  const [, year, month, day] = match.map(Number)

  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day)
}

// Filter content based on active status, date range, and valid URL
function filterContent(items, now) {
  if (!Array.isArray(items)) return []

  return items.filter(item => {
    // 1. Only active content
    if (!item.IsActive) return false

    // 2. Validate date range
    if (item.StartDate) {
      if (now < getLocalDay(item.StartDate)) return false
    }

    if (item.EndDate) {
      if (now > getLocalDay(item.EndDate, { endOfDay: true })) return false
    }

    // 3. Valid media URL
    if (!item.MediaUrl) return false

    return true
  })
}

// Sort content by Order field (if exists), then by CreatedAt
function sortContent(items) {
  return [...items].sort((a, b) => {
    // Priority: Order field
    if (a.Order != null && b.Order != null) {
      return a.Order - b.Order
    }

    // Fallback: CreatedAt field
    if (a.CreatedAt && b.CreatedAt) {
      return new Date(a.CreatedAt) - new Date(b.CreatedAt)
    }

    return 0
  })
}

export function useScreensaverContent() {
  const [availableContent, setAvailableContent] = useState([])
  const [evaluatedAt, setEvaluatedAt] = useState(() => Date.now())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Use context instead of Redux to get synced values from BroadcastChannel
  const { company: CompanyId, branchOffice: BranchOfficeId } =
    useCustomerDisplay()

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true)
        setError(null)

        const payload = {
          CompanyId,
          BranchOfficeId,
          PageNumber: 1,
          PageSize: 100,
        }

        const response = await searchCustomerDisplaySettings(payload)

        // API returns data in response.data.Data structure
        const rawContent = response?.data?.Data || []

        // Normalize content structure (FileUrl -> MediaUrl, VIDEO -> video)
        setAvailableContent(normalizeContent(rawContent))
      } catch (err) {
        console.error(
          '[useScreensaverContent] Failed to fetch screensaver content:',
          err
        )
        setError(err)
        setAvailableContent([]) // Fallback to empty array (will show logo)
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if we have CompanyId and BranchOfficeId
    if (CompanyId && BranchOfficeId) {
      fetchContent()
    } else {
      setLoading(false)
    }
  }, [CompanyId, BranchOfficeId])

  // The CD can stay open for days, so the date range is re-evaluated when the
  // day changes instead of only when the content is fetched.
  useEffect(() => {
    const now = new Date(evaluatedAt)
    const nextDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    )
    const timeout = setTimeout(
      () => setEvaluatedAt(Date.now()),
      nextDay.getTime() - now.getTime()
    )

    return () => clearTimeout(timeout)
  }, [evaluatedAt])

  const content = useMemo(
    () => sortContent(filterContent(availableContent, new Date(evaluatedAt))),
    [availableContent, evaluatedAt]
  )

  return { content, loading, error }
}
